import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { tokenService } from '../services/tokenService';
import { setAuthenticated } from '../redux/AuthReducer/authSlice';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
    children: React.ReactElement;
}

/**
 * ProtectedRoute component that redirects to login if no current user is set
 * Preserves the original destination URL for post-login redirect
 * Allows access to protected routes only when user is authenticated
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const dispatch = useAppDispatch();
    const { user, loading } = useAppSelector((state) => state.currentUser);
    const { isAuthenticated } = useAppSelector((state) => state.login);
    const location = useLocation();

    // Check token validity on mount
    useEffect(() => {
        const hasToken = tokenService.hasToken();
        if (hasToken && !isAuthenticated) {
            dispatch(setAuthenticated(true));
        } else if (!hasToken && isAuthenticated) {
            dispatch(setAuthenticated(false));
        }
    }, [dispatch, isAuthenticated]);

    // Still loading user data, show loading spinner
    if (loading && !user) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    // Check if user has valid token and is authenticated
    const hasToken = tokenService.hasToken();
    
    // No token or not authenticated, redirect to login
    if (!hasToken || !isAuthenticated || !user) {
        // Build returnUrl with full path including search params
        const returnUrl = `${location.pathname}${location.search}`;
        const encodedReturnUrl = encodeURIComponent(returnUrl);
        
        // Redirect to login with returnUrl as query parameter
        return <Navigate to={`/login?returnUrl=${encodedReturnUrl}`} replace />;
    }

    // User is authenticated, render the protected content
    return children;
};

export default ProtectedRoute;